import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate, useParams } from 'react-router';
import JobCard from 'components/JobCard/Index';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import JobFilters from 'components/feature/jobFilters';
import { JobService } from 'services/jobs/job';
import { IJob, IJobFilters } from 'core/interface/job';
import { IMe } from 'core/interface/me';
import NoData from 'components/shared/NoData/Index';
import { JobCardActionEnum } from 'core/enum/jobCardAction';
import useLocales from 'hooks/useLocales';
import Image from 'components/image/Image';
import OrderBySelect from 'components/shared/OrderBySelect/Index';
import Pagination from '@material-ui/core/Pagination';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import {
  DEFAULT_ORDERING,
  ScoreOrdering
} from 'core/constants/ordering';

import './Index.scss';

const FilterJobs: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filter, setFilter] = useState<IJobFilters>();
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const me = useSelector((state: IMe) => state.user).user;
  const { id } = useParams();
  const targetRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [ordering, setOrdering] = useState<ScoreOrdering>(DEFAULT_ORDERING);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    getJobs(limit, offset);
  }, [filter, limit, offset, ordering, me?.candidate_id]);

  useEffect(() => {
    setPage(1);
    setOffset(0);
  }, [filter]);

  const handleOrderingChange = (value: ScoreOrdering): void => {
    setOrdering(value);
    setPage(1);
    setOffset(0);
  };

  const getJobs = async (limit?: number, offset?: number): Promise<void> => {
    try {
      JobService.getJobs({
        limit,
        offset,
        ordering: ordering || 'score',
        score_candidate_id: me?.candidate_id || undefined,
        industries: filter?.industry ? Number(filter.industry) : undefined,
        skills: filter?.computer_skill?.length
          ? filter.computer_skill.map(Number)
          : undefined,
        degrees: filter?.degree_type ? Number(filter.degree_type) : undefined,
        locations: filter?.location?.id
          ? Number(filter.location.id)
          : undefined,
        // salaries: filter?.salary?.length
        //   ? filter.salary.map(Number)
        //   : undefined,
        compensations: filter?.compensations?.length
          ? filter.compensations.map(Number)
          : undefined,
        work_exps: filter?.career_level?.length
          ? filter.career_level.map(Number)
          : undefined,
        languages: filter?.languages?.length
          ? filter.languages.map((item) => Number(item.id))
          : undefined,
        company: id ? +id : undefined,
        is_remote:
          filter?.is_remote !== undefined ? filter.is_remote : undefined,
        contracts: filter?.contracts?.length
          ? filter.contracts.map(Number)
          : undefined,
        company_sizes: filter?.company_sizes?.length
          ? filter.company_sizes.map(Number)
          : undefined
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleFilterOpen = (): void => {
    setShowFilter((prev) => !prev);
  };

  const handleChangeScroll = (): void => {
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({
        block: 'start'
      });
    }
  };

  const handleActionComplete = async (): Promise<void> => {
    await getJobs(limit, offset);
  };

  useEffect(() => {
    handleChangeScroll();
  }, [targetRef]);

  return (
    <div
      className="filterJobs"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <div className="fjobs" ref={targetRef}>
        <div className="top">
          {!id && (
            <div
              className="back_buttons skillio-back-button"
              onClick={() => navigate(CANDIDATE_ROUTES.BASE)}
            >
              <Image name="arrow-left.svg" />
              {t('Back')}
            </div>
          )}
          {/** this was intended for smaller screens but styling and logic is way off */}
          {/* <div className="back_buttons" onClick={handleFilterOpen}>
            <Image name="filtering.svg" />
            {t('Open Filter')}
          </div> */}
        </div>
        <div className="filter-and-jobs">
          <div className={`filtering ${showFilter ? 'show' : ''}`}>
            <JobFilters applyFilter={setFilter} />
          </div>
          <div className="fjobs__matches">
            <div className="fjobs__matches__header">
              <OrderBySelect value={ordering} onChange={handleOrderingChange} />
            </div>
            <div className="fjobs__matches__cards">
              {jobs?.results?.length === 0 ? (
                <NoData />
              ) : (
                jobs?.results?.map((item: IJob, index: number) => (
                  <div key={index} className="fjobs__matches__card">
                    <JobCard
                      id={item.id}
                      title={item.title}
                      sub_title={item.description}
                      icon={
                        item.company.logo ??
                        '/static/images/recrout-logo-letter.jpg'
                      }
                      actions={[
                        JobCardActionEnum.MORE_INFO,
                        JobCardActionEnum.APPLY,
                        JobCardActionEnum.PROFILE_VIEW
                      ]}
                      matches={[
                        { percent: +item.score, title: 'Talent Match' },
                        { percent: +item.culture_score, title: 'Cultural Fit' }
                      ]}
                      isApplied={!!item.is_applied}
                      assessmentRequired={item.assessment_required}
                      onActionComplete={handleActionComplete}
                      competencies={item.competencies}
                      competencyMinScores={item?.competency_min_scores}
                      cultureCompetencies={
                        item.culture_profile?.competencies || []
                      }
                      cultureProfile={item.culture_profile}
                      postedAt={item.created_at}
                    />
                  </div>
                ))
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((jobs?.count || 0) / limit)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="main" style={{ flex: 1 }}>
        {/* ... main content ... */}
      </div>
      <Footer />
    </div>
  );
};

export default FilterJobs;
